import { useEffect } from "react";

import { isSaasDistribution } from "utils/distribution";
import { markFeaturebaseIframesAsTopLayer } from "utils/featurebase";

import { Theme } from "../types/Theme";
import { AccountContext } from "../views/AccountWrapper";
import { ThemeContext } from "../views/Theme";
import useTypedContext from "./useTypedContext";

const isSaas = isSaasDistribution();

/**
 * Code here is based on the Remix example from here: https://help.featurebase.app/help/articles/1261560-install-feedback-widget
 */
const initFeaturebase = () => {
  const existingScript = document.getElementById("featurebase-sdk");
  if (existingScript) {
    return;
  }

  const script = document.createElement("script");
  script.src = "https://do.featurebase.app/js/sdk.js";
  script.id = "featurebase-sdk";
  document.head.appendChild(script);

  if (typeof window.Featurebase !== "function") {
    window.Featurebase = function () {
      if (window.Featurebase) {
        (window.Featurebase.q = window.Featurebase.q || []).push(
          // eslint-disable-next-line prefer-rest-params
          arguments
        );
      }
    };
  }
};

const useInitFeaturebase = (showChangelogUpdate: (show: boolean) => void) => {
  const { currentTheme } = useTypedContext(ThemeContext);
  const { viewer, accountId, accountName } = useTypedContext(AccountContext);

  useEffect(() => {
    // Only initialize Featurebase in SaaS environments
    if (!isSaas) return;

    initFeaturebase();
  }, []);

  useEffect(() => {
    if (!isSaas || !viewer) {
      return;
    }

    const controller = new AbortController();
    const theme = [Theme.Dark, Theme.HighContrastDark].includes(currentTheme) ? "dark" : "light";

    fetch("/featurebase/identity", { method: "POST", signal: controller.signal })
      .then((res) => res.json())
      .then(({ token }: { token: string }) => {
        if (!token) {
          return;
        }

        window.Featurebase?.(
          "identify",
          { organization: "spacelift", featurebaseJwt: token },
          (err) => {
            if (err) {
              console.warn("Featurebase error", err);
              return;
            }

            window.Featurebase?.(
              "initialize_feedback_widget",
              {
                organization: "spacelift",
                featurebaseJwt: token,
                theme,
                locale: "en",
                metadata: {
                  since: String(viewer.since),
                  userId: viewer.id,
                  ulid: accountId,
                },
              },
              markFeaturebaseIframesAsTopLayer
            );

            window.Featurebase?.(
              "init_changelog_widget",
              {
                organization: "spacelift",
                featurebaseJwt: token,
                theme,
                locale: "en",
                popup: {
                  enabled: true,
                  autoOpenForNewUpdates: false,
                },
              },
              (error, data) => {
                if (error) {
                  return;
                }
                if (data?.action === "unreadChangelogsCountChanged") {
                  showChangelogUpdate(data.unreadCount > 0);
                }
              }
            );
          }
        );
      })
      .catch(() => {
        // no-op: skip widget initialization if the JWT fetch fails
      });

    return () => controller.abort();
  }, [accountId, accountName, currentTheme, viewer, showChangelogUpdate]);
};

export default useInitFeaturebase;
